import { TimeZoneModel } from '@fleet/model';
import { DateTime, Duration } from 'luxon';
import { interval, Observable } from 'rxjs';
import { filter, map, startWith, take } from 'rxjs/operators';
interface CountdownOptions {
  tickDuration?: number;
  emitZero?: boolean;
  emitInitial?: boolean;
}

export function countdown(
  ticks: number,
  options: CountdownOptions = {}
): Observable<number> {
  const tickDuration = options.tickDuration ?? 1000;
  const emitZero = options.emitZero ?? true;
  const emitInitial = options.emitInitial ?? true;

  const countdown$ = interval(tickDuration).pipe(
    take(ticks),
    map((n) => ticks - 1 - n),
    filter((value) => emitZero || value !== 0)
  );

  if (emitInitial) return countdown$.pipe(startWith(ticks));
  else return countdown$;
}

const dateFormats: { [key: string]: string } = {
  'en-US': 'MM/dd/yyyy',
  'en-GB': 'dd/MM/yyyy',
  'en-AU': 'dd/MM/yyyy',
  'fr-FR': 'dd/MM/yyyy',
  // Add other locale mappings as needed
};

export const dateFormatByLocale = (locale: string) => {
  return dateFormats[locale] || 'dd/MM/yyyy';
};

/** Normalize API datetime values (ISO string or Luxon) to a valid DateTime. */
export function toDateTime(
  value: DateTime | string | null | undefined,
): DateTime | null {
  if (value == null) {
    return null;
  }
  if (DateTime.isDateTime(value)) {
    return value.isValid ? value : null;
  }
  const parsed = DateTime.fromISO(String(value), { setZone: true });
  return parsed.isValid ? parsed : null;
}

/** True when both values are real dates and end is after start (minute precision). */
export function isValidDateRange(
  start: DateTime | string | null | undefined,
  end: DateTime | string | null | undefined,
): boolean {
  const startAt = toDateTime(start);
  const endAt = toDateTime(end);
  return (
    !!startAt && !!endAt && endAt.startOf('minute') > startAt.startOf('minute')
  );
}

export const humanReadableDuration = (duration: Duration) => {
  if (!duration || !duration.isValid) {
    return null;
  }

  const minutes = duration.as('minutes');
  if (isNaN(minutes)) {
    return null;
  }
  const roundedMinutes = Math.round(minutes);
  // Convert the rounded minutes back to a Duration
  const roundedDuration = Duration.fromObject({ minutes: roundedMinutes });
  // Format based on whether it's under or over 60 minutes
  const humanReadable =
    roundedMinutes < 60
      ? roundedDuration.toHuman({ unitDisplay: 'short' }) // e.g., "45 minutes"
      : roundedDuration
          .shiftTo('hours', 'minutes')
          .toHuman({ unitDisplay: 'short' }); // e.g., "1 hr, 2 min"

  return humanReadable;
};

/** Validation floor: now in job timezone, minute precision. Never cached. */
export function getValidationMinDateFromTimezone(
  timezone?: TimeZoneModel | null,
): DateTime {
  const tzId = timezone?.id;
  if (tzId) {
    return DateTime.now().setZone(tzId).startOf('minute');
  }
  return DateTime.now().startOf('minute');
}

/** Picker floor: validation min minus one calendar day. */
export function getPickerMinDateFromValidationMin(
  validationMin: DateTime,
): DateTime {
  return validationMin.minus({ days: 1 });
}

export interface ToWallClockInTimezoneOptions {
  dateOnly?: boolean;
}

/**
 * Reinterpret a picker DateTime's wall calendar + clock in the given timezone.
 * Uses Y/M/D/H/m from the value (not instant conversion across zones).
 */
export function toWallClockInTimezone(
  value: DateTime,
  timezoneId?: string | null,
  options?: ToWallClockInTimezoneOptions,
): DateTime | null {
  if (!value?.isValid) {
    return null;
  }

  const tzId = timezoneId ?? null;
  if (!tzId) {
    return options?.dateOnly ? value.startOf('day') : value.startOf('minute');
  }

  if (options?.dateOnly) {
    return DateTime.fromObject(
      { year: value.year, month: value.month, day: value.day },
      { zone: tzId },
    ).startOf('day');
  }

  return DateTime.fromObject(
    {
      year: value.year,
      month: value.month,
      day: value.day,
      hour: value.hour,
      minute: value.minute,
      second: 0,
      millisecond: 0,
    },
    { zone: tzId },
  ).startOf('minute');
}

export interface CombineDateAndTimeOptions {
  defaultToStartOfDay?: boolean;
}

/**
 * Build a zoned DateTime from picker date + time wall clock in job timezone.
 * Uses calendar year/month/day from the date part in tzId, not the raw UTC instant.
 */
export function combineDateAndTimeInTimezone(
  datePart: DateTime,
  timePart: DateTime | null | undefined,
  timezone?: TimeZoneModel | null,
  options?: CombineDateAndTimeOptions,
): DateTime | null {
  if (!datePart?.isValid) {
    return null;
  }

  const tzId = timezone?.id ?? datePart.zoneName ?? DateTime.local().zoneName;
  // Use picker Y/M/D as wall calendar day in the job zone (not instant conversion).
  const wallDate = DateTime.fromObject(
    { year: datePart.year, month: datePart.month, day: datePart.day },
    { zone: tzId },
  );

  if (timePart?.isValid) {
    return wallDate
      .set({
        hour: timePart.hour,
        minute: timePart.minute,
        second: 0,
        millisecond: 0,
      })
      .startOf('minute');
  }

  if (options?.defaultToStartOfDay) {
    return wallDate.startOf('day');
  }

  return null;
}
